import { findRegimen, type RegimenLookup } from "@/data/meal-plan";
import type { DayOfWeek, WeekPlan } from "@/types/meal-plan-state";
import { getMealChoices } from "./random-plan";

/**
 * Migrate a week plan from legacy optionIndex-based selections to optionId-based.
 *
 * Saved plans in the DB may still use `{ optionIndex: number, count: number }`.
 * This function converts them to `{ optionId: string, count: number }` by looking
 * up the option at the given index and reading its `id` field.
 *
 * Returns the (possibly mutated) plan and whether any migration occurred.
 */
export function migrateWeekPlan(
  plan: WeekPlan,
  lookup: RegimenLookup = findRegimen,
): { plan: WeekPlan; migrated: boolean } {
  let migrated = false;

  for (const day of Object.keys(plan) as DayOfWeek[]) {
    const dayPlan = plan[day];
    if (!dayPlan.regimenId || !dayPlan.meals) continue;

    const regimen = lookup(dayPlan.regimenId);
    if (!regimen) continue;

    for (let mi = 0; mi < dayPlan.meals.length; mi++) {
      const meal = regimen.meals[mi];
      if (!meal) continue;
      const mealSels = dayPlan.meals[mi];
      if (!mealSels) continue;
      const choices = getMealChoices(meal, dayPlan.mealMenus?.[mi]);

      for (let ci = 0; ci < mealSels.length; ci++) {
        const cgSels = mealSels[ci];
        if (!cgSels || cgSels.skipped) continue;
        const group = choices[ci];
        if (!group) continue;

        for (const sel of cgSels.selections) {
          const raw = sel as Record<string, unknown>;
          // Legacy format: has optionIndex but no optionId
          if ("optionIndex" in raw && !("optionId" in raw)) {
            const idx = raw.optionIndex as number;
            const opt = group.options[idx];
            if (opt) {
              raw.optionId = opt.id;
            } else {
              // Option index out of bounds — use string index as fallback
              raw.optionId = String(idx);
            }
            delete raw.optionIndex;
            migrated = true;
          }
        }
      }
    }
  }

  return { plan, migrated };
}
